import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUp, ArrowDown } from "lucide-react";
import { api, errMsg, fmtAZN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BetControl } from "@/components/game/BetControl";
import { Panel, Stat } from "./usePlay";
import { sfx } from "@/lib/sound";
import { Card } from "./PlayingCard";

export const HiLoGame = ({ mode }) => {
  const { updateUser } = useAuth();
  const [g, setG] = useState(null);
  const [bet, setBet] = useState(1);
  const [busy, setBusy] = useState(false);
  const active = g?.status === "active";

  useEffect(() => { api.get("/games/hilo/active").then(({ data }) => data && setG(data)); }, []);

  const call = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/games/hilo/${path}`, body);
      sfx.flip();
      setG(data);
      updateUser({ balance: data.balance, demo_balance: data.demo_balance });
      if (data.status === "lost") { sfx.lose(); toast.error("Səhv təxmin. Mərc uduzuldu"); }
      else if (data.status === "won") { sfx.cashout(); toast.success(`Qazanc: +${fmtAZN(data.win)}`); }
      else if (data.correct) sfx.gem();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const cur = g?.cards[g.cards.length - 1];
  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-6" data-testid="hilo-game">
      <Panel className="order-2 lg:order-1">
        <div><p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Mərc</p><BetControl value={bet} onChange={setBet} disabled={active} testid="hilo-bet" /></div>
        {active && g.mode !== mode && <p className="text-xs rounded-lg bg-amber-500/10 text-amber-300 p-2" data-testid="hilo-mode-note">Bu oyun {g.mode === "demo" ? "Demo" : "Real"} rejimində başlanıb</p>}
        {active && (
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Əmsal" value={`x${g.multiplier}`} testid="hilo-mult" />
            <Stat label="Qazanc" value={fmtAZN(g.bet * g.multiplier)} />
          </div>
        )}
        {active ? (
          <button disabled={busy || g.cards.length < 2} onClick={() => call("cashout", { game_id: g.game_id })} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="hilo-cashout-btn">Götür {fmtAZN(g.bet * g.multiplier)}</button>
        ) : (
          <button disabled={busy} onClick={() => call("start", { bet, mode })} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="hilo-start-btn">Oyuna başla</button>
        )}
      </Panel>
      <div className="rounded-3xl p-6 sm:p-10 order-1 lg:order-2 min-h-[420px] flex flex-col items-center justify-center gap-6 border-4 border-[#5b3a1a]" style={{ background: "radial-gradient(ellipse at center, #166534 0%, #0b3b20 70%, #052e16 100%)" }}>
        {cur ? (
          <motion.div key={g.cards.length} initial={{ rotateY: 90, scale: 0.8 }} animate={{ rotateY: 0, scale: 1 }} transition={{ duration: 0.35 }} style={{ transformPerspective: 800 }} data-testid="hilo-card">
            <Card c={cur} />
          </motion.div>
        ) : (
          <div className="w-36 h-52 sm:w-44 sm:h-64 rounded-xl border-4 border-white/80 shadow-xl" style={{ background: "repeating-linear-gradient(45deg,#c4112f 0 8px,#7B001C 8px 16px)" }} />
        )}
        {active && (
          <div className="grid grid-cols-2 gap-3 w-full max-w-md">
            <button disabled={busy} onClick={() => call("guess", { game_id: g.game_id, guess: "higher" })} className="rounded-2xl py-4 bg-emerald-600 hover:bg-emerald-500 transition-colors font-display font-bold flex flex-col items-center" data-testid="hilo-higher-btn">
              <span className="flex items-center gap-1"><ArrowUp size={18} /> Yuxarı / bərabər</span>
              <span className="text-xs font-normal opacity-90">{g.odds.higher.chance}% · x{g.odds.higher.mult}</span>
            </button>
            <button disabled={busy} onClick={() => call("guess", { game_id: g.game_id, guess: "lower" })} className="rounded-2xl py-4 bg-red-600 hover:bg-red-500 transition-colors font-display font-bold flex flex-col items-center" data-testid="hilo-lower-btn">
              <span className="flex items-center gap-1"><ArrowDown size={18} /> Aşağı / bərabər</span>
              <span className="text-xs font-normal opacity-90">{g.odds.lower.chance}% · x{g.odds.lower.mult}</span>
            </button>
          </div>
        )}
        {g && <div className="flex gap-2 flex-wrap justify-center" data-testid="hilo-history">{g.cards.slice(0, -1).slice(-10).map((c, i) => <Card key={i} c={c} small />)}</div>}
        {g && !active && <p className={`font-display font-bold ${g.status === "won" ? "text-emerald-300" : "text-red-300"}`} data-testid="hilo-status">{g.status === "won" ? `Qazandınız ${fmtAZN(g.win)}` : "Uduzdunuz"}</p>}
      </div>
    </div>
  );
};
